import React, { useEffect, useState } from "react";
import { X, ExternalLink, AlertCircle } from "lucide-react";
import { resolveEmbed } from "../lib/videoEmbed";

/**
 * InlineVideoPlayer — v79.118
 *
 * Full-viewport modal video/audio/media player. Opens instantly when a
 * URL is embeddable, or shows a clear "Open externally" prompt when
 * the provider is known to block iframe embedding.
 *
 * UX / responsiveness rules
 *  - Modal fills the viewport on mobile (< 640 px) with the player
 *    pinned to the visual centre; on desktop it's a centred card up to
 *    92 vw / 88 vh.
 *  - Aspect ratio is honoured (16/9, 9/16, or auto for audio) so
 *    portrait YouTube Shorts and vertical direct-videos never letter-
 *    box awkwardly.
 *  - Safe-area insets are respected (iOS notch, Android nav bar).
 *  - `overflow: hidden` on body while open so the underlying page
 *    doesn't scroll under the modal.
 *  - Escape key + backdrop tap close the modal.
 *  - `preload="metadata"` on native tags so we don't burn cellular
 *    data before the user hits Play.
 */
export default function InlineVideoPlayer({ url, title = "", onClose }) {
    const [errored, setErrored] = useState(false);
    const embed = resolveEmbed(url);

    // Lock body scroll while modal is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    // Close on ESC.
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    // v79.118 — Browser back-button integration. Push a history state
    // when the modal opens so Android's back gesture / Chrome's back
    // button / iOS swipe-from-edge close THIS modal instead of exiting
    // the page. Works on all browsers that support History API (that
    // is: every browser we care about, incl. the Capacitor WebView).
    useEffect(() => {
        try {
            window.history.pushState({ adukooModal: "video" }, "");
            const onPop = () => onClose?.();
            window.addEventListener("popstate", onPop);
            return () => {
                window.removeEventListener("popstate", onPop);
                // If the modal is closing via any path other than the
                // back button, quietly clean up the history entry so
                // the user's future "back" navigates as expected.
                if (window.history.state && window.history.state.adukooModal === "video") {
                    try { window.history.back(); } catch { /* ignore */ }
                }
            };
        } catch { /* history API blocked — ESC + close button still work */ }
    }, [onClose]);

    const openExternally = () => {
        try {
            window.open(url, "_blank", "noopener,noreferrer");
        } catch { /* ignore */ }
    };

    // Nothing to play — never render an empty modal.
    if (!url) return null;

    // Blocked-provider path: no iframe attempt, straight to "open in <app>".
    const blocked = !embed || embed.blocked || errored;
    const providerLabel = (embed && !embed.blocked) ? embed.provider : (embed?.provider || "");
    const reason = embed?.reason || "This link cannot be played inside Adukoo.";

    // Aspect-ratio & height sizing.
    let aspectClass = "aspect-video";        // 16/9 default
    let inlineStyle = {};
    if (embed && !embed.blocked && !errored) {
        if (embed.aspect === "9/16") { aspectClass = "aspect-[9/16]"; }
        else if (embed.aspect === "auto") {
            aspectClass = "";
            inlineStyle = { height: `${embed.height || 380}px` };
        }
    }

    return (
        <div
            className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={title || "Media player"}
            data-testid="inline-video-player"
            style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 12px)", paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)" }}
        >
            <div
                className={`relative w-full ${aspectClass === "aspect-[9/16]" ? "max-w-[min(92vw,420px)]" : "max-w-[min(92vw,1080px)]"} max-h-[88vh] bg-black rounded-2xl overflow-hidden shadow-2xl`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button — always reachable, keeps its tap target */}
                <button
                    onClick={onClose}
                    aria-label="Close player"
                    data-testid="inline-video-close"
                    className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur"
                    style={{ top: "max(env(safe-area-inset-top, 0px), 8px)" }}
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Blocked-provider prompt (no embed attempted) */}
                {blocked ? (
                    <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-white min-h-[220px]" data-testid="inline-video-blocked">
                        <AlertCircle className="w-10 h-10 text-amber-400" />
                        <div className="font-display font-black text-lg">
                            Can&apos;t play here
                        </div>
                        <p className="text-sm text-white/70 max-w-xs">
                            {reason}
                            {providerLabel ? ` Tap below to open it in ${providerLabel}.` : " Tap below to open it in a new tab."}
                        </p>
                        <button
                            onClick={openExternally}
                            data-testid="inline-video-open-external"
                            className="mt-2 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-aduku-yellow text-aduku-navy font-black text-sm hover:brightness-110"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Open{providerLabel ? ` in ${providerLabel}` : " externally"}
                        </button>
                    </div>
                ) : embed.embed === "iframe" ? (
                    <div className={`${aspectClass} w-full`} style={inlineStyle}>
                        <iframe
                            src={embed.src}
                            title={title || embed.provider}
                            allow={embed.allow}
                            allowFullScreen={embed.allowFullscreen}
                            referrerPolicy="strict-origin-when-cross-origin"
                            loading="eager"
                            onError={() => setErrored(true)}
                            className="w-full h-full border-0"
                            data-testid={`inline-video-iframe-${embed.type}`}
                        />
                    </div>
                ) : embed.embed === "video" ? (
                    <div className={`${aspectClass} w-full`} style={inlineStyle}>
                        <video
                            src={embed.src}
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                            onError={() => setErrored(true)}
                            className="w-full h-full bg-black"
                            data-testid="inline-video-native"
                        />
                    </div>
                ) : embed.embed === "audio" ? (
                    <div className="p-6 flex flex-col items-center gap-3" style={inlineStyle}>
                        <div className="text-white font-bold text-sm">{title || embed.provider}</div>
                        <audio
                            src={embed.src}
                            controls
                            autoPlay
                            preload="metadata"
                            onError={() => setErrored(true)}
                            className="w-full max-w-md"
                            data-testid="inline-audio-native"
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
